import { BusElement } from '../element/element-bus.js'
import { AddObjectRecord } from '../history/record.js'
import { DotParser, DOT_TYPE_MAP } from './dot-parser.js'

const LAYOUT = {
  originX: 200,
  originY: 40,
  hSpacing: 110,
  vSpacing: 100,
  gridCols: 10,
  busPadding: 120,
}

/**
 * 将解析后的 DOT 路径构建为拓扑图
 */
export class DotBuilder {
  /**
   * @param {import('../topology.js').Topology} topology
   * @param {import('./dot-parser.js').DotNodeToken[][]} paths
   * @param {Record<string, Function>} constructors
   */
  constructor(topology, paths, constructors) {
    this.topology = topology
    this.paths = paths
    this.constructors = constructors
    this.parser = new DotParser()
    /** @type {Map<string, import('../graph-element.js').GraphElement>} */
    this.registry = new Map()
    /** @type {{ from: string, to: string }[]} */
    this.edges = []
    /** @type {Map<string, string[]>} */
    this.adjacency = new Map()
  }

  async build() {
    this.buildGraph()
    await this.createElements()
    this.layout()
    this.connectAll()
    this.topology.painter.paint()
  }

  buildGraph() {
    this.trieRoot = createTrieNode(null, 'root')
    const root = this.trieRoot

    for (const steps of this.paths) {
      /** @type {{ trie: object, nodeId: string }[]} */
      let layers = [{ trie: root, nodeId: 'root' }]

      for (let i = 0; i < steps.length; i++) {
        const step = steps[i]
        const nextLayers = []

        for (const layer of layers) {
          for (const token of step) {
            const childIds = this.resolveChildIds(layer.trie, token)

            for (const childId of childIds) {
              if (i > 0) {
                this.addEdge(layer.nodeId, childId)
              }
              nextLayers.push({
                trie: layer.trie.children.get(childId),
                nodeId: childId,
              })
            }
          }
        }

        layers = nextLayers
      }
    }
  }

  /**
   * 在 Trie 中查找或创建子节点，匿名节点按父节点位置区分，具名节点按参数复用
   * @param {object} parent
   * @param {import('./dot-parser.js').DotNodeToken} token
   * @returns {string[]}
   */
  resolveChildIds(parent, token) {
    if (token.count > 1 && !token.param) {
      return this.parser.expandKeys(token).map((expandedKey) => {
        return this.getOrCreateChild(parent, token, expandedKey)
      })
    }

    return [this.getOrCreateChild(parent, token, token.key)]
  }

  /**
   * @param {object} parent
   * @param {import('./dot-parser.js').DotNodeToken} token
   * @param {string} preferredId
   */
  getOrCreateChild(parent, token, preferredId) {
    if (token.param) {
      if (!parent.children.has(preferredId)) {
        parent.children.set(preferredId, createTrieNode(token, preferredId, preferredId))
      }
      return preferredId
    }

    if (token.count > 1) {
      if (!parent.children.has(preferredId)) {
        parent.children.set(preferredId, createTrieNode(token, preferredId, preferredId))
      }
      return preferredId
    }

    const existing = [...parent.children.values()].find((child) => child.matchKey === token.type)
    if (existing) {
      return existing.nodeId
    }

    const nodeId = `${token.type}@${parent.nodeId}`
    parent.children.set(nodeId, createTrieNode(token, nodeId, token.type))
    return nodeId
  }

  addEdge(from, to) {
    this.edges.push({ from, to })
    if (!this.adjacency.has(from)) {
      this.adjacency.set(from, [])
    }
    const children = this.adjacency.get(from)
    if (!children.includes(to)) {
      children.push(to)
    }
  }

  async createElements() {
    const tokenMap = this.collectTokens()
    const tasks = []

    for (const [key, token] of tokenMap) {
      if (this.registry.has(key)) continue
      tasks.push(this.createElement(key, token))
    }

    await Promise.all(tasks)
  }

  /**
   * @returns {Map<string, import('./dot-parser.js').DotNodeToken>}
   */
  collectTokens() {
    const map = new Map()

    const visit = (trieNode) => {
      if (!trieNode.token || trieNode.nodeId === 'root') {
        trieNode.children.forEach(visit)
        return
      }

      const token = trieNode.token
      const key = trieNode.nodeId

      if (!map.has(key)) {
        if (token.count > 1 && !token.param) {
          const index = Number(key.split(':').pop())
          map.set(key, {
            ...token,
            key,
            param: String(index + 1),
          })
        } else {
          map.set(key, { ...token, key })
        }
      }

      trieNode.children.forEach(visit)
    }

    visit(this.trieRoot)
    return map
  }

  /**
   * @param {string} key
   * @param {import('./dot-parser.js').DotNodeToken} token
   */
  async createElement(key, token) {
    const ctorKey = DOT_TYPE_MAP[token.type]
    const Ctor = this.constructors[ctorKey]

    if (!Ctor) {
      throw new Error(`未找到元件构造器: ${token.type}`)
    }

    const label = this.parser.getLabel(token)
    let elem

    if (token.type === 'bus') {
      elem = Ctor.create(this.topology, LAYOUT.originX, LAYOUT.originY)
      elem.label?.setAttr('text', label)
    } else {
      elem = Ctor.create(this.topology, 0, 0, { text: label })
    }

    this.registry.set(key, elem)
    this.topology.storage.addElement(elem)
    this.topology.history.push(new AddObjectRecord(this.topology, elem))
    await waitElementReady(elem)
    return elem
  }

  layout() {
    const depths = this.computeDepths()
    const levels = new Map()

    for (const [key, depth] of depths) {
      if (!levels.has(depth)) levels.set(depth, [])
      levels.get(depth).push(key)
    }

    const sortedDepths = [...levels.keys()].sort((a, b) => a - b)

    for (const depth of sortedDepths) {
      const keys = levels.get(depth)
      if (depth === 0) {
        this.layoutBus(keys)
        continue
      }
      this.layoutLevel(keys, depth)
    }

    this.fitBusWidth()
  }

  computeDepths() {
    const depths = new Map()
    const busKey = this.findBusKey()
    if (!busKey) return depths

    const queue = [busKey]
    depths.set(busKey, 0)

    while (queue.length) {
      const key = queue.shift()
      const depth = depths.get(key)
      for (const child of this.adjacency.get(key) || []) {
        if (!depths.has(child)) {
          depths.set(child, depth + 1)
          queue.push(child)
        }
      }
    }

    for (const key of this.registry.keys()) {
      if (!depths.has(key)) {
        depths.set(key, 1)
      }
    }

    return depths
  }

  findBusKey() {
    for (const [key, elem] of this.registry) {
      if (elem instanceof BusElement) {
        return key
      }
    }
    return null
  }

  layoutBus(keys) {
    const key = keys[0]
    const bus = this.getElement(key)
    if (!bus) return
    bus.g.x(LAYOUT.originX)
    bus.g.y(LAYOUT.originY)
  }

  layoutLevel(keys, depth) {
    const y = LAYOUT.originY + depth * LAYOUT.vSpacing
    const isBulkGrid = keys.length > 1 && keys.every((k) => k.startsWith('eblock:*:'))

    if (isBulkGrid) {
      this.layoutGrid(keys, y)
      return
    }

    const cols = Math.max(1, Math.min(keys.length, LAYOUT.gridCols))
    const rowWidth = (cols - 1) * LAYOUT.hSpacing
    const startX = LAYOUT.originX - rowWidth / 2

    keys.forEach((key, index) => {
      const col = index % cols
      const row = Math.floor(index / cols)
      const elem = this.getElement(key)
      elem.g.x(startX + col * LAYOUT.hSpacing - elem.width / 2)
      elem.g.y(y + row * (LAYOUT.vSpacing * 0.75))
    })
  }

  layoutGrid(keys, y) {
    const cols = Math.min(LAYOUT.gridCols, keys.length)
    const rowWidth = (cols - 1) * LAYOUT.hSpacing
    const startX = LAYOUT.originX - rowWidth / 2

    keys.forEach((key, index) => {
      const col = index % cols
      const row = Math.floor(index / cols)
      const elem = this.getElement(key)
      elem.g.x(startX + col * LAYOUT.hSpacing - elem.width / 2)
      elem.g.y(y + row * 72)
    })
  }

  fitBusWidth() {
    const busKey = this.findBusKey()
    if (!busKey) return

    const bus = this.getElement(busKey)
    const children = this.adjacency.get(busKey) || []
    if (!children.length) return

    const childElems = children.map((k) => this.getElement(k)).filter(Boolean)
    if (!childElems.length) return

    const minX = Math.min(...childElems.map((e) => e.cx))
    const maxX = Math.max(...childElems.map((e) => e.cx))
    const width = Math.max(400, maxX - minX + LAYOUT.busPadding * 2)

    bus.g.x(minX - LAYOUT.busPadding)
    bus.keyShape.width(width)
    bus.updateLabelPosition()
  }

  connectAll() {
    for (const { from, to } of this.edges) {
      const source = this.getElement(from)
      const target = this.getElement(to)
      if (source && target) {
        this.connect(source, target)
      }
    }
  }

  /**
   * @param {import('../graph-element.js').GraphElement} source
   * @param {import('../graph-element.js').GraphElement} target
   */
  connect(source, target) {
    const { graph } = this.topology.storage
    let sourcePort
    let targetPort

    if (source instanceof BusElement) {
      sourcePort = source.addDownPort(target.cx)
    } else {
      sourcePort = source.ports.portDown
    }

    if (target instanceof BusElement) {
      targetPort = target.addUpPort(source.cx)
    } else {
      targetPort = target.ports.portUp
    }

    graph.addEdge({ node: source, port: sourcePort }, { node: target, port: targetPort })
  }

  /**
   * @param {string} key
   */
  getElement(key) {
    return this.registry.get(key)
  }
}

/**
 * @param {import('../graph-element.js').GraphElement} elem
 */
function waitElementReady(elem) {
  if (elem.keyShape) {
    return Promise.resolve(elem)
  }
  return new Promise((resolve) => {
    elem.onLoad(() => resolve(elem))
  })
}

/**
 * @param {import('./dot-parser.js').DotNodeToken|null} token
 * @param {string} nodeId
 * @param {string} [matchKey]
 */
function createTrieNode(token, nodeId, matchKey = nodeId) {
  return {
    token,
    nodeId,
    matchKey,
    children: new Map(),
  }
}
